import React, { useState } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FileText, TrendingUp, Package, DollarSign, Clock, Warehouse } from 'lucide-react';
import { 
  MOCK_REPORTS, 
  CYCLE_TIME_REPORT, 
  WAREHOUSE_INVENTORY,
  MONTHLY_TRANSPORT_COSTS,
  DASHBOARD_STATS 
} from '../services/mockData';
import '../styles/Reports.css';

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState('utilization');

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Prepare data for pie chart - Assets by Type
  const assetsByType = [
    { name: 'FLC', value: 2550, color: '#3b82f6' },
    { name: 'WMC', value: 1400, color: '#10b981' },
    { name: 'PSS', value: 2300, color: '#f59e0b' }
  ];

  // Prepare warehouse inventory data for bar chart
  const warehouseInventoryData = Object.keys(WAREHOUSE_INVENTORY).map(warehouse => ({
    name: warehouse,
    ...WAREHOUSE_INVENTORY[warehouse]
  }));

  // Prepare transportation cost data
  const transportCostData = MONTHLY_TRANSPORT_COSTS.map(route => ({
    name: route.route.replace(' -> ', '→'),
    trips: route.trips,
    cost: route.totalCost / 1000, // Convert to thousands
    avgCost: route.avgCostPerTrip / 1000
  }));

  const totalMonthlyTransportCost = MONTHLY_TRANSPORT_COSTS.reduce((sum, r) => sum + r.totalCost, 0);
  const totalMonthlyTrips = MONTHLY_TRANSPORT_COSTS.reduce((sum, r) => sum + r.trips, 0);

  return (
    <div className="reports-page">
      <div className="page-header">
        <div>
          <h1>Reports & Analytics</h1>
          <p className="page-subtitle">Comprehensive asset tracking and cost analysis</p>
        </div>
      </div>

      {/* Report Type Selector */}
      <div className="report-selector">
        <button 
          className={`report-tab ${selectedReport === 'utilization' ? 'active' : ''}`}
          onClick={() => setSelectedReport('utilization')}
        >
          <TrendingUp size={18} />
          Utilization
        </button>
        <button 
          className={`report-tab ${selectedReport === 'cycleTime' ? 'active' : ''}`}
          onClick={() => setSelectedReport('cycleTime')}
        >
          <Clock size={18} />
          Cycle Time
        </button>
        <button 
          className={`report-tab ${selectedReport === 'inventory' ? 'active' : ''}`}
          onClick={() => setSelectedReport('inventory')}
        >
          <Warehouse size={18} />
          Inventory
        </button>
        <button 
          className={`report-tab ${selectedReport === 'transport' ? 'active' : ''}`}
          onClick={() => setSelectedReport('transport')}
        >
          <DollarSign size={18} />
          Transport Costs
        </button>
      </div>

      {/* Utilization Report */}
      {selectedReport === 'utilization' && (
        <div className="report-content">
          <div className="report-card">
            <div className="card-header">
              <h2>
                <TrendingUp size={20} />
                Overall Asset Utilization
              </h2>
              <p className="card-subtitle">
                {DASHBOARD_STATS.averageUtilization}% of {DASHBOARD_STATS.totalAssets.toLocaleString()} total assets
              </p>
            </div>

            <div className="charts-grid">
              <div className="chart-container">
                <h3>Assets by Type</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={assetsByType}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {assetsByType.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-container">
                <h3>Utilization by Asset Type</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={MOCK_REPORTS.utilizationByType}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="type" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="utilization" fill="#3b82f6" name="Utilization %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="detailed-breakdown">
              <h3>Asset Status by Type</h3>
              <table className="breakdown-table">
                <thead>
                  <tr>
                    <th className="col-type">Asset Type</th>
                    <th className="col-count">Total Count</th>
                    <th className="col-transit">In Transit<br/><span className="percentage">(60%)</span></th>
                    <th className="col-loading">Loading/<br/>Unloading<br/><span className="percentage">(13%)</span></th>
                    <th className="col-idle">Idle<br/><span className="percentage">(22%)</span></th>
                    <th className="col-maintenance">Maintenance<br/><span className="percentage">(5%)</span></th>
                    <th className="col-utilization">Utilization</th>
                  </tr>
                </thead>
                <tbody>
                  {MOCK_REPORTS.utilizationByType.map(item => (
                    <tr key={item.type}>
                      <td className="col-type"><strong>{item.type}</strong></td>
                      <td className="col-count">{item.count.toLocaleString()}</td>
                      <td className="col-transit">{item.inTransit.toLocaleString()}</td>
                      <td className="col-loading">{item.loading.toLocaleString()}</td>
                      <td className="col-idle">{item.idle.toLocaleString()}</td>
                      <td className="col-maintenance">{item.maintenance.toLocaleString()}</td>
                      <td className="col-utilization"><strong>{item.utilization}%</strong></td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td className="col-type"><strong>Total</strong></td>
                    <td className="col-count"><strong>6,250</strong></td>
                    <td className="col-transit"><strong>3,750</strong></td>
                    <td className="col-loading"><strong>812</strong></td>
                    <td className="col-idle"><strong>1,375</strong></td>
                    <td className="col-maintenance"><strong>313</strong></td>
                    <td className="col-utilization"><strong>73%</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Cycle Time Report */}
      {selectedReport === 'cycleTime' && (
        <div className="report-content">
          <div className="report-card">
            <div className="card-header">
              <h2>
                <Clock size={20} />
                Weekly Cycle Time Report
              </h2>
              <p className="card-subtitle">
                Time taken for assets to return to origin warehouse
              </p>
            </div>

            <div className="cycle-time-table-container">
              <table className="cycle-time-table">
                <thead>
                  <tr>
                    <th>Asset Type</th>
                    <th>Asset ID</th>
                    <th>Origin Warehouse</th>
                    <th>Cycle Time (Days)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {CYCLE_TIME_REPORT.map((item, index) => (
                    <tr key={index}>
                      <td><span className="asset-type-badge">{item.assetType}</span></td>
                      <td><strong>{item.assetId}</strong></td>
                      <td>{item.originWarehouse}</td>
                      <td>
                        <strong>{item.cycleTimeDays} days</strong>
                      </td>
                      <td>
                        {item.cycleTimeDays > 30 ? (
                          <span className="status-badge status-warning">Long Cycle</span>
                        ) : item.cycleTimeDays > 20 ? (
                          <span className="status-badge status-medium">Medium</span>
                        ) : (
                          <span className="status-badge status-good">Normal</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="cycle-time-summary">
              <div className="summary-stat">
                <span className="summary-label">Average Cycle Time:</span>
                <span className="summary-value">
                  {Math.round(CYCLE_TIME_REPORT.reduce((sum, item) => sum + item.cycleTimeDays, 0) / CYCLE_TIME_REPORT.length)} days
                </span>
              </div>
              <div className="summary-stat">
                <span className="summary-label">Shortest Cycle:</span>
                <span className="summary-value">
                  {Math.min(...CYCLE_TIME_REPORT.map(item => item.cycleTimeDays))} days
                </span>
              </div>
              <div className="summary-stat">
                <span className="summary-label">Longest Cycle:</span>
                <span className="summary-value">
                  {Math.max(...CYCLE_TIME_REPORT.map(item => item.cycleTimeDays))} days
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Report */}
      {selectedReport === 'inventory' && (
        <div className="report-content">
          <div className="report-card">
            <div className="card-header">
              <h2>
                <Warehouse size={20} />
                Inventory by Warehouse
              </h2>
              <p className="card-subtitle">
                Asset distribution across Leadec warehouse locations
              </p>
            </div>

            <div className="chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={warehouseInventoryData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="FLC" fill="#3b82f6" />
                  <Bar dataKey="PSS" fill="#10b981" />
                  <Bar dataKey="WMC" fill="#f59e0b" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="inventory-table-container">
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Warehouse</th>
                    <th>FLC</th>
                    <th>PSS</th>
                    <th>WMC</th>
                    <th>Total</th>
                    <th>% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(WAREHOUSE_INVENTORY).map(warehouse => {
                    const inv = WAREHOUSE_INVENTORY[warehouse];
                    const percentage = ((inv.total / 6250) * 100).toFixed(1);
                    return (
                      <tr key={warehouse}>
                        <td><strong>{warehouse}</strong></td>
                        <td>{inv.FLC}</td>
                        <td>{inv.PSS}</td>
                        <td>{inv.WMC}</td>
                        <td><strong>{inv.total}</strong></td>
                        <td>{percentage}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Transportation Costs Report */}
      {selectedReport === 'transport' && (
        <div className="report-content">
          <div className="report-card">
            <div className="card-header">
              <h2>
                <DollarSign size={20} />
                Monthly Transportation Cost Summary
              </h2>
              <p className="card-subtitle">
                Route-wise transportation expenses for the month
              </p>
            </div>

            <div className="cost-summary-cards">
              <div className="cost-summary-card">
                <h3>Total Monthly Cost</h3>
                <p className="cost-value">₹{(totalMonthlyTransportCost / 100000).toFixed(2)}L</p>
                <p className="cost-meta">{totalMonthlyTrips} trips</p>
              </div>
              <div className="cost-summary-card">
                <h3>Average Cost per Trip</h3>
                <p className="cost-value">₹{Math.round(totalMonthlyTransportCost / totalMonthlyTrips).toLocaleString()}</p>
                <p className="cost-meta">Across all routes</p>
              </div>
            </div>

            <div className="chart-container">
              <h3>Cost by Route</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={transportCostData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-15} textAnchor="end" height={80} />
                  <YAxis label={{ value: 'Cost (₹ thousands)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip formatter={(value) => `₹${value}K`} />
                  <Legend />
                  <Bar dataKey="cost" fill="#3b82f6" name="Total Cost (₹K)" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="transport-cost-table-container">
              <table className="transport-cost-table">
                <thead>
                  <tr>
                    <th>Route</th>
                    <th>Total Trips</th>
                    <th>Total Cost</th>
                    <th>Avg Cost/Trip</th>
                  </tr>
                </thead>
                <tbody>
                  {MONTHLY_TRANSPORT_COSTS.map((route, index) => (
                    <tr key={index}>
                      <td><strong>{route.route}</strong></td>
                      <td>{route.trips}</td>
                      <td>₹{(route.totalCost / 1000).toFixed(1)}K</td>
                      <td>₹{(route.avgCostPerTrip / 1000).toFixed(1)}K</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td><strong>Total</strong></td>
                    <td><strong>{totalMonthlyTrips}</strong></td>
                    <td><strong>₹{(totalMonthlyTransportCost / 100000).toFixed(2)}L</strong></td>
                    <td><strong>₹{Math.round(totalMonthlyTransportCost / totalMonthlyTrips / 1000)}K</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;

